import type { MissionAssessmentListItemDto } from '@@types/api.types';
import { TECHNOLOGY_TAB_HISTORY_ITEM_CLASS } from '@components/pages/technology/components/shared/technology-container-query.constants';
import { Avatar } from '@components/shared/avatar.component';
import { cn } from '@lib/utils';
import { formatDate } from '@utils/date.util';
import { CircleUserRound } from 'lucide-react';
import { FC } from 'react';
import { MISSION_ASSESSMENT_ACCESS_RESTRICTED_MESSAGE } from '../../../../mission-assessment/mission-assessment.constants';
import {
  MaSidebarAssessmentList,
  type MaSidebarAssessmentListRenderItemParams,
} from './ma-sidebar-assessment-list.component';

type MaAssessmentHistoryListProps = {
  isOverviewActive: boolean;
  isLoading?: boolean;
  onSelectAssessment: (assessmentId: string) => void;
  onSelectOverview: () => void;
  selectedAssessmentId: string | null;
  versions: MissionAssessmentListItemDto[];
};

export const MaAssessmentHistoryList: FC<MaAssessmentHistoryListProps> = (props) => {
  const { isLoading, isOverviewActive, onSelectAssessment, onSelectOverview, selectedAssessmentId, versions } = props;

  return (
    <MaSidebarAssessmentList
      emptyText="No assessments yet."
      isLoading={isLoading}
      isOverviewActive={isOverviewActive}
      onSelectAssessment={onSelectAssessment}
      onSelectOverview={onSelectOverview}
      renderItem={(renderItemProps) => <MaAssessmentHistoryItem {...renderItemProps} />}
      selectedAssessmentId={selectedAssessmentId}
      title="Mission Assessment History"
      titleClassName="my-1 text-base font-medium text-gray-700"
      versions={versions}
    />
  );
};

type MaAssessmentHistoryItemProps = MaSidebarAssessmentListRenderItemParams;

const MaAssessmentHistoryItem: FC<MaAssessmentHistoryItemProps> = (props) => {
  const { isSelected, onPrefetch, onSelect, version } = props;

  const expertName = version.expert?.profile?.fullName ?? 'Unknown Expert';
  const expertTitle = version.expert?.profile?.title ?? 'Expert';
  const isAccessRestricted = Boolean(version.accessRestricted);
  const restrictedMessage = version.accessRestrictedMessage ?? MISSION_ASSESSMENT_ACCESS_RESTRICTED_MESSAGE;

  return (
    <button
      className={cn(
        'flex items-center gap-2 rounded-md relative min-h-10 p-2 text-left transition-all duration-200',
        TECHNOLOGY_TAB_HISTORY_ITEM_CLASS,
        isAccessRestricted
          ? 'cursor-not-allowed border border-amber-200 bg-amber-50 text-amber-800'
          : isSelected
            ? 'cursor-pointer ring-2 ring-[#3D2EFFCC] ring-inset bg-linear-to-b bg-[#3D2EFF26] text-[#3D2EFF]'
            : 'cursor-pointer bg-gray-100 text-gray-600 hover:ring-2 hover:ring-[#3D2EFFCC]',
      )}
      onClick={() => {
        if (isAccessRestricted) {
          return;
        }
        onSelect();
      }}
      onMouseEnter={() => {
        if (isAccessRestricted) {
          return;
        }
        onPrefetch();
      }}
      type="button"
    >
      <Avatar
        altText={expertName}
        fallbackIcon={<CircleUserRound className="h-full w-full text-gray-400" />}
        imageUrl={version.expert?.avatarUrl}
        initialText={expertName}
        size="sm"
        className="shrink-0 grow-0 items-center flex justify-center"
      />

      <div className="flex flex-col gap-1 min-w-0 flex-1">
        <span className="text-xs font-medium truncate">{expertName}</span>
        <span className="text-xs text-gray-500 truncate">{expertTitle}</span>
        {isAccessRestricted && <span className="text-[11px] text-amber-800">{restrictedMessage}</span>}
      </div>

      <div className="flex flex-col absolute right-2 top-2 gap-1 ml-auto mb-auto text-xs whitespace-nowrap text-gray-500">
        <span>{formatDate(version.updatedAt)}</span>
      </div>
    </button>
  );
};
